import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { respondByChangeWords } from '@modules/common/components/item-handover/handover-respond-by';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { ItemChangesClient, type ItemChange, type ItemChangesPath } from './item-changes.client';

@Component({
  selector: 'app-item-changes',
  standalone: true,
  imports: [DateFormatPipe],
  providers: [ItemChangesClient],
  templateUrl: './item-changes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemChangesComponent {
  public readonly path = input.required<ItemChangesPath>();
  public readonly itemId = input.required<string>();
  public readonly tenantId = input.required<string>();
  private readonly client = inject(ItemChangesClient);
  private readonly displayZone = inject(DisplayTimeZoneService);
  protected readonly page = linkedSignal(() => {
    this.path();
    this.itemId();
    this.tenantId();
    return 0;
  });
  protected readonly rows = signal<readonly ItemChange[]>([]);
  protected readonly lines = computed(() => {
    const zone = this.displayZone.timeZone();
    return this.rows().map((row) => ({
      uid: row.uid,
      author: row.author,
      at: row.at,
      words: row.respondBy ? respondByChangeWords(row.respondBy, zone) : row.excerpt,
    }));
  });
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  protected readonly retryable = signal(false);
  private readonly revision = signal(0);
  private readonly load = effect((onCleanup) => {
    const path = this.path();
    const itemId = this.itemId();
    const tenantId = this.tenantId();
    const offset = this.page() * 20;
    this.revision();
    this.rows.set([]);
    this.total.set(0);
    this.failure.set(null);
    this.retryable.set(false);
    this.loading.set(true);
    const subscription = this.client.read(path, itemId, tenantId, offset).subscribe({
      next: ({ items, total }) => {
        this.rows.set(items);
        this.total.set(total);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.loading.set(false);
        if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 0)) return;
        if (error instanceof HttpErrorResponse && error.status === 403) {
          const body: unknown = error.error;
          this.failure.set(
            body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
              ? body.message
              : 'Activity could not load.',
          );
          return;
        }
        this.failure.set('Activity could not load.');
        this.retryable.set(true);
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });

  protected retry(): void {
    this.revision.update((revision) => revision + 1);
  }
  protected previous(): void {
    this.page.update((page) => Math.max(0, page - 1));
  }
  protected next(): void {
    if ((this.page() + 1) * 20 < this.total()) this.page.update((page) => page + 1);
  }
}
